// Markdown → structured sections parser for Email List Builder results

export interface ParsedBlock {
  type: "label-content" | "bullets" | "text" | "email-card";
  label?: string;
  content?: string;
  items?: string[];
  emailNumber?: number;
  subject?: string;
  subjectVariations?: string[];
  body?: string;
}

export interface ParsedSection {
  title: string;
  rawText: string;
  blocks: ParsedBlock[];
}

function cleanTitle(title: string): string {
  return title
    .replace(/^\d+[\.\)]\s*/, "")
    .replace(/^step\s+\d+[:\s]*/i, "")
    .replace(/^[📧📋✉️🎯📝💡🔥🚀📌📣]+\s*/, "")
    .trim();
}

export function parseSections(raw: string): ParsedSection[] {
  const cleaned = raw
    .replace(/^(?:okay|here['']s|below is|sure|alright|great)[^#\n]*?\n{1,3}/i, "")
    .replace(/^[^\n#]*(?:comprehensive|designed for|tailored|here is|i've created)[^\n]*\n{1,3}/i, "");

  const sectionRegex = /^#{1,2}\s+(.+)$/gm;
  const matches: { title: string; index: number; end: number }[] = [];
  let match: RegExpExecArray | null;
  while ((match = sectionRegex.exec(cleaned)) !== null) {
    matches.push({ title: match[1].replace(/[*_`]/g, "").trim(), index: match.index, end: match.index + match[0].length });
  }

  const splits: { title: string; content: string }[] = [];
  for (let i = 0; i < matches.length; i++) {
    const contentStart = matches[i].end;
    const contentEnd = i + 1 < matches.length ? matches[i + 1].index : cleaned.length;
    const content = cleaned.slice(contentStart, contentEnd).trim();
    if (content) splits.push({ title: matches[i].title, content });
  }

  if (splits.length === 0 && cleaned.trim()) {
    splits.push({ title: "Results", content: cleaned.trim() });
  }

  return splits.map((s) => ({
    title: cleanTitle(s.title),
    rawText: s.content,
    blocks: parseBlocks(s.content, s.title),
  }));
}

function parseBlocks(content: string, sectionTitle: string): ParsedBlock[] {
  const lowerTitle = sectionTitle.toLowerCase();
  const isEmailSequence =
    lowerTitle.includes("email") &&
    (lowerTitle.includes("sequence") || lowerTitle.includes("follow") || lowerTitle.includes("welcome") || lowerTitle.includes("series"));

  if (isEmailSequence) {
    const blocks: ParsedBlock[] = [];
    const emailSplits = content.split(/(?=###?\s+(?:email|day|message)\s*\d)/i);
    for (const emailBlock of emailSplits) {
      const trimmed = emailBlock.trim();
      if (!trimmed) continue;
      const numMatch = trimmed.match(/(?:email|day|message)\s*(\d+)/i);
      const emailNumber = numMatch ? parseInt(numMatch[1]) : null;
      if (emailNumber !== null) {
        // Primary subject
        const subjectMatch = trimmed.match(/\*{0,2}subject(?:\s*line)?\*{0,2}\s*[:：]\s*(.+)/i);
        const subject = subjectMatch ? subjectMatch[1].replace(/[*`"]/g, "").trim() : undefined;

        // Subject line variations
        const variationRegex = /\*{0,2}(?:variation|v|option|alt(?:ernative)?)\s*\d?\*{0,2}\s*[:：]\s*(.+)/gi;
        const subjectVariations: string[] = [];
        if (subject) subjectVariations.push(subject);
        let vm: RegExpExecArray | null;
        while ((vm = variationRegex.exec(trimmed)) !== null) {
          subjectVariations.push(vm[1].replace(/[*`"]/g, "").trim());
        }

        // Also check for numbered subject lines like "1. Subject" or "- Subject" after a "Subject Line Variations" header
        const variationBlockMatch = trimmed.match(/(?:subject\s*line\s*variations?|a\/b\s*test(?:ing)?)\s*[:：]?\s*\n([\s\S]*?)(?=\n\*{0,2}body|$)/i);
        if (variationBlockMatch) {
          const lines = variationBlockMatch[1].split("\n");
          for (const line of lines) {
            const l = line.trim();
            const item = l.replace(/^[-•*]\s+/, "").replace(/^\d+[\.\)]\s+/, "").replace(/[*`"]/g, "").trim();
            if (item && item.length > 5 && !subjectVariations.includes(item)) {
              subjectVariations.push(item);
            }
          }
        }

        const bodyContent = trimmed
          .replace(/^###?\s+.+\n/, "")
          .replace(/\*{0,2}subject(?:\s*line)?\*{0,2}\s*[:：]\s*.+\n?/gi, "")
          .replace(/\*{0,2}(?:variation|v|option|alt(?:ernative)?)\s*\d?\*{0,2}\s*[:：]\s*.+\n?/gi, "")
          .replace(/(?:subject\s*line\s*variations?|a\/b\s*test(?:ing)?)\s*[:：]?\s*\n[\s\S]*?(?=\n\*{0,2}body)/i, "")
          .trim();

        blocks.push({
          type: "email-card",
          emailNumber,
          subject: subjectVariations[0] || subject,
          subjectVariations: subjectVariations.length > 1 ? subjectVariations : [],
          body: bodyContent.replace(/^\*{0,2}body\*{0,2}\s*[:：]\s*/i, "").trim(),
        });
      } else {
        blocks.push(...parseGenericBlocks(trimmed));
      }
    }
    if (blocks.some((b) => b.type === "email-card")) return blocks;
  }

  return parseGenericBlocks(content);
}

function parseGenericBlocks(content: string): ParsedBlock[] {
  const blocks: ParsedBlock[] = [];
  const lines = content.split("\n");
  let currentBullets: string[] = [];

  const flushBullets = () => {
    if (currentBullets.length > 0) {
      blocks.push({ type: "bullets", items: [...currentBullets] });
      currentBullets = [];
    }
  };

  for (const line of lines) {
    const l = line.trim();
    if (!l) { flushBullets(); continue; }
    const labelMatch = l.match(
      /^\*{0,2}(headline|subheadline|sub-headline|subject(?:\s*line)?|body|cta|call[\s-]to[\s-]action|hook|description|title|tagline|preview text|from name|send time|goal|format|deliverable|topic)\*{0,2}\s*[:：]\s*(.+)/i
    );
    if (labelMatch) { flushBullets(); blocks.push({ type: "label-content", label: labelMatch[1].trim(), content: labelMatch[2].replace(/[*`]/g, "").trim() }); continue; }
    if (/^[-•*]\s+/.test(l) || /^\d+[\.\)]\s+/.test(l)) {
      currentBullets.push(l.replace(/^[-•*]\s+/, "").replace(/^\d+[\.\)]\s+/, "").replace(/\*{2}(.+?)\*{2}/g, "$1").trim());
      continue;
    }
    if (/^#{3,4}\s+/.test(l)) { flushBullets(); blocks.push({ type: "label-content", label: l.replace(/^#{3,4}\s+/, "").replace(/[*`]/g, "").trim(), content: "" }); continue; }
    flushBullets();
    blocks.push({ type: "text", content: l.replace(/\*{2}(.+?)\*{2}/g, "$1") });
  }
  flushBullets();
  return blocks;
}
